import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { Part, TargetEntry } from './converters';
import ActionPart from './parts/ActionPart';
import AssertionPart from './parts/AssertionPart';
import AuthStatePart from './parts/AuthStatePart';
import DefineVariablePart from './parts/DefineVariablePart';
import ReceiveEmailPart from './parts/ReceiveEmailPart';
import SelectDropdownPart from './parts/SelectDropdownPart';
import TargetPart from './parts/TargetPart';
import UploadPart from './parts/UploadPart';
import ValuePart from './parts/ValuePart';
import SpecialLoginPart from './parts/SpecialLoginPart';
import { useCallback } from 'react';
import PositionPart from './parts/PositionPart';
import FetchRequestPart from './parts/FetchRequestPart';
import TimeoutPart from './parts/TimeoutPart';
import { match, P } from 'ts-pattern';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export function OpRendererPart({
  testContentId,
  operation,
  index,
  secondTargetIndex,
  part,
  parts,
  updateParts,
  handleSubmit,
  readonly,
  setEditing,
  parentContexts,
}: {
  testContentId: string;
  operation: SelectedOperation;
  index: number;
  secondTargetIndex: number;
  part: Part | { type: 'slot' };
  parts: Part[];
  updateParts: (parts: Part[], index: number) => void;
  handleSubmit: ({
    parts,
    refreshStepDescription,
  }: {
    parts: Part[];
    refreshStepDescription: boolean;
  }) => Promise<void>;
  readonly?: boolean;
  setEditing: (isEditing: boolean) => void;
  parentContexts?: OperationParentContext[];
}) {
  const toggleEditing = useCallback((editing: boolean) => setEditing(editing && !readonly), [readonly, setEditing]);

  return (
    <div
      className="flex min-w-0 items-center gap-2"
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
    >
      {index === secondTargetIndex && <div key="to">to</div>}
      {match(part)
        .with({ type: 'action' }, (part) => (
          <ActionPart
            action={part.action}
            options={part.options}
          />
        ))
        .with({ type: 'assertion' }, (part) => (
          <AssertionPart
            assertionPart={part}
            toggleEditing={toggleEditing}
            readonly={readonly}
            updateFunc={async (assertionPart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...assertionPart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'defineVariable' }, (part) => (
          <DefineVariablePart
            testContentId={testContentId}
            operation={operation}
            operationId={operation.id}
            readonly={readonly}
            part={part}
            pageAlias={operation.pageAlias}
            toggleEditing={toggleEditing}
            updateFunc={async (valuePart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...valuePart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
            parentContexts={parentContexts}
          />
        ))
        .with({ type: 'receiveEmail' }, (part) => (
          <ReceiveEmailPart
            testContentId={testContentId}
            operationId={operation.id}
            part={part}
            toggleEditing={toggleEditing}
            updateFunc={async (receiveEmailPart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...receiveEmailPart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
            parentContexts={parentContexts}
          />
        ))
        .with({ type: 'fetchRequest' }, (part) => (
          <FetchRequestPart
            part={part}
            toggleEditing={toggleEditing}
            updateFunc={async (fetchRequestPart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...fetchRequestPart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'upload' }, (part) => (
          <UploadPart
            files={part.files.map((x) => ({ ...x }))}
            toggleEditing={toggleEditing}
            updateFunc={async (files) => {
              const newParts = [...parts.slice(0, index), { ...part, files }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'value' }, (part) => (
          <ValuePart
            testContentId={testContentId}
            operationId={operation.id}
            readonly={readonly}
            part={part}
            toggleEditing={toggleEditing}
            parts={parts}
            parentContexts={parentContexts}
            updateFunc={async (valuePart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...valuePart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'target' }, (part) => (
          <TargetPart
            testContentId={testContentId}
            operationId={operation.id}
            target={part.target}
            readonly={readonly}
            toggleEditing={setEditing}
            updateFunc={async (target: TargetEntry) => {
              const newParts = [...parts.slice(0, index), { ...part, target }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: part.target.name !== target.name });
            }}
            scrollLevel={operation.actionOp?.action === 'scroll' ? 'element' : undefined}
          />
        ))
        .with({ type: 'selectDropdown' }, (part) => (
          <SelectDropdownPart
            option={part.selectedOption}
            options={part.options}
            readonly={readonly}
            toggleEditing={toggleEditing}
            updateFunc={async (selected) => {
              const newParts = [
                ...parts.slice(0, index),
                { ...part, selectedOption: selected },
                ...parts.slice(index + 1),
              ];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'authState' }, (part) => (
          <AuthStatePart
            part={part}
            readonly={readonly}
            toggleEditing={toggleEditing}
            updateFunc={async (authStatePart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...authStatePart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'specialLogin' }, (part) => (
          <SpecialLoginPart
            part={part}
            readonly={readonly}
            toggleEditing={toggleEditing}
            updateFunc={async (specialLoginPart) => {
              const newParts = [...parts.slice(0, index), { ...part, ...specialLoginPart }, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
          />
        ))
        .with({ type: 'position' }, (part) => <PositionPart position={part.position} />)
        .with({ type: 'emptyTarget' }, () => (
          <TargetPart
            testContentId={testContentId}
            operationId={operation.id}
            target={{ name: '', locator: '' }}
            readonly={readonly}
            toggleEditing={setEditing}
            updateFunc={async (target: TargetEntry) => {
              // If both locator and name are defined, we convert to a target part
              const newPart =
                target.locator.trim() && target.name.trim() ?
                  { type: 'target' as const, target }
                : { type: 'emptyTarget' as const, target };

              const newParts = [...parts.slice(0, index), newPart, ...parts.slice(index + 1)];
              updateParts(newParts, index + 1);
              await handleSubmit({ parts: newParts, refreshStepDescription: true });
            }}
            scrollLevel="page"
          />
        ))
        .with({ type: 'timeout' }, (part) => (
          <TimeoutPart
            timeoutMs={part.timeoutMs}
            operation={operation}
            testContentId={testContentId}
            readonly={readonly}
          />
        ))
        .with({ type: P.union('slot', 'componentCall') }, () => null)
        .exhaustive()}
    </div>
  );
}
